import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import {
  checkPaymentStatus,
  downloadInvoice,
} from "../../redux/phonepay/phonepePaymentSlice";

const PaymentStatus = () => {
  const { transactionId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    paymentData,
    status,
    statusLoading,
    userDetails,
    planDetails,
    amount,
    invoiceUrl,
    invoiceNumber,
  } = useSelector((state) => state.phonepe);

  const [localStatus, setLocalStatus] = useState("checking");
  const [invoiceDownloading, setInvoiceDownloading] = useState(false);

  // =====================================================
  // FETCH PAYMENT STATUS
  // =====================================================

  const checkStatus = async () => {
    try {
      const result = await dispatch(
        checkPaymentStatus(transactionId)
      ).unwrap();

      const state = result?.state;

      if (state === "COMPLETED") {
        setLocalStatus("success");
      } else if (state === "FAILED") {
        setLocalStatus("failed");
      } else {
        setLocalStatus("pending");
      }
    } catch (err) {
      console.log("STATUS ERROR:", err);

      setLocalStatus("failed");
    }
  };

  // =====================================================
  // AUTO REFRESH PAYMENT STATUS
  // =====================================================

  useEffect(() => {
    if (!transactionId) return;

    // First check immediately
    checkStatus();

    // Then check every 5 seconds
    const interval = setInterval(() => {
      checkStatus();
    }, 5000);

    return () => clearInterval(interval);
  }, [transactionId]);

  // =====================================================
  // DOWNLOAD INVOICE PDF
  // =====================================================

  const handleDownloadInvoice = async () => {
    if (!transactionId) {
      console.log("Transaction ID not available");
      return;
    }

    try {
      setInvoiceDownloading(true);

      await dispatch(
        downloadInvoice(transactionId)
      ).unwrap();

    } catch (error) {
      console.log(
        "INVOICE DOWNLOAD ERROR:",
        error
      );

      alert(
        error?.message ||
        "Unable to download invoice"
      );
    } finally {
      setInvoiceDownloading(false);
    }
  };

  // =====================================================
  // CLOSE
  // =====================================================

  const handleClose = () => {
    localStorage.removeItem(
      "pendingTransactionId"
    );

    navigate("/");
  };

  // =====================================================
  // UI CONFIG
  // =====================================================

  const statusUI = {
    success: {
      badge:
        "bg-green-100 text-green-700 border-green-300",

      title:
        "Payment Successful 🎉",

      bar:
        "bg-green-500",
    },

    pending: {
      badge:
        "bg-yellow-100 text-yellow-700 border-yellow-300",

      title:
        "Payment Processing ⏳",

      bar:
        "bg-yellow-500",
    },

    failed: {
      badge:
        "bg-red-100 text-red-700 border-red-300",

      title:
        "Payment Failed ❌",

      bar:
        "bg-red-500",
    },

    checking: {
      badge:
        "bg-blue-100 text-blue-700 border-blue-300",

      title:
        "Checking Payment...",

      bar:
        "bg-blue-500",
    },
  };

  const ui =
    statusUI[localStatus] ||
    statusUI.checking;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">

      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border overflow-hidden">

        {/* =================================================
            TOP BAR
        ================================================= */}

        <div
          className={`h-2 w-full ${ui.bar}`}
        />

        {/* =================================================
            HEADER
        ================================================= */}

        <div className="p-5 text-center">

          <div className="flex justify-end">

            <button
              onClick={handleClose}
              className="text-gray-500 hover:text-gray-700 text-sm"
            >
              ✖ Close
            </button>

          </div>

          <div
            className={`
              inline-block
              px-3
              py-1
              text-sm
              font-semibold
              rounded-full
              border
              ${ui.badge}
            `}
          >
            {localStatus.toUpperCase()}
          </div>

          <h2 className="mt-3 text-xl font-bold text-gray-800">
            {ui.title}
          </h2>

          {statusLoading && (
            <p className="text-xs text-gray-400 mt-2">
              Refreshing status...
            </p>
          )}

        </div>

        {/* =================================================
            PAYMENT DETAILS
        ================================================= */}

        {paymentData && (
          <div className="px-5 pb-4 space-y-3 text-sm border-t pt-4">

            {/* TRANSACTION */}

            <p className="flex justify-between gap-4">
              <span className="font-semibold">
                Transaction
              </span>

              <span className="text-right break-all">
                {paymentData.transactionId ||
                  transactionId ||
                  "-"}
              </span>
            </p>

            {/* STATUS */}

            <p className="flex justify-between">
              <span className="font-semibold">
                Status
              </span>

              <span>
                {paymentData.state ||
                  status ||
                  "-"}
              </span>
            </p>

            {/* AMOUNT */}

            <p className="flex justify-between">
              <span className="font-semibold">
                Amount
              </span>

              <span className="font-semibold">
                ₹
                {amount ||
                  paymentData.amount ||
                  0}
              </span>
            </p>

            <hr />

            {/* =================================================
                USER DETAILS
            ================================================= */}

            <p className="flex justify-between">
              <span className="font-semibold">
                User
              </span>

              <span>
                {userDetails?.username ||
                  paymentData?.user?.username ||
                  "-"}
              </span>
            </p>

            {/* MOBILE */}

            <p className="flex justify-between">
              <span className="font-semibold">
                Mobile
              </span>

              <span>
                {userDetails?.phone ||
                  paymentData?.user?.mobile ||
                  "-"}
              </span>
            </p>

            {/* =================================================
                PLAN DETAILS
            ================================================= */}

            <p className="flex justify-between">
              <span className="font-semibold">
                Plan
              </span>

              <span>
                {planDetails?.planName ||
                  paymentData?.plan?.planName ||
                  "-"}
              </span>
            </p>

            {/* =================================================
                INVOICE NUMBER
            ================================================= */}

            <p className="flex justify-between items-center">
              <span className="font-semibold">
                Invoice No
              </span>

              <span>
                {invoiceNumber ||
                  paymentData?.invoiceNumber ||
                  "Not generated yet"}
              </span>
            </p>

            {/* =================================================
                INVOICE PDF
            ================================================= */}

            <div className="flex justify-between items-center pt-2">

              <span className="font-semibold">
                Invoice
              </span>

              {/* <div>

                {invoiceUrl ||
                invoiceNumber ||
                paymentData?.invoiceNumber ? (

                  <button
                    type="button"
                    onClick={handleDownloadInvoice}
                    disabled={invoiceDownloading}
                    className={`
                      px-3
                      py-1.5
                      rounded-lg
                      text-sm
                      font-semibold
                      text-white
                      transition
                      ${
                        invoiceDownloading
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-blue-600 hover:bg-blue-700"
                      }
                    `}
                  >
                    {invoiceDownloading
                      ? "Downloading..."
                      : "Download PDF"}
                  </button>

                ) : (

                  <span className="text-gray-400">
                    Not available
                  </span>

                )}

              </div> */}

            </div>

          </div>
        )}

        {/* =================================================
            SUCCESS
        ================================================= */}

        {localStatus === "success" && (
          <div className="p-5 border-t text-center">

            <p className="text-green-600 font-medium">
              Subscription Activated 🎉
            </p>

            {/* INVOICE BUTTON */}

            {(invoiceUrl ||
              invoiceNumber ||
              paymentData?.invoiceNumber) && (
              <button
                type="button"
                onClick={handleDownloadInvoice}
                disabled={invoiceDownloading}
                className={`
                  mt-4
                  w-full
                  py-2
                  rounded-lg
                  font-semibold
                  text-white
                  transition
                  ${
                    invoiceDownloading
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-blue-600 hover:bg-blue-700"
                  }
                `}
              >
                {invoiceDownloading
                  ? "Downloading Invoice..."
                  : "Download Invoice PDF"}
              </button>
            )}

            <button
              onClick={handleClose}
              className="mt-3 w-full bg-gray-200 hover:bg-gray-300 py-2 rounded-lg transition"
            >
              Close
            </button>

          </div>
        )}

        {/* =================================================
            PENDING
        ================================================= */}

        {localStatus === "pending" && (
          <div className="p-5 border-t text-center text-yellow-600 font-medium">

            Waiting for payment confirmation...

          </div>
        )}

        {/* =================================================
            FAILED
        ================================================= */}

        {localStatus === "failed" && (
          <div className="p-5 border-t text-center">

            <p className="text-red-600 font-semibold">
              Payment Failed ❌
            </p>

            <button
              onClick={handleClose}
              className="
                mt-4
                w-full
                bg-gray-200
                hover:bg-gray-300
                text-gray-800
                font-semibold
                py-2
                rounded-lg
                transition
              "
            >
              Close
            </button>

          </div>
        )}

      </div>
    </div>
  );
};

export default PaymentStatus;